import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useApplication } from '../context/ApplicationContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PersonalInfoStep from '../components/steps/PersonalInfoStep';
import ContactInfoStep from '../components/steps/ContactInfoStep';
import EmploymentInfoStep from '../components/steps/EmploymentInfoStep';
import AccountInfoStep from '../components/steps/AccountInfoStep';
import ProgressBar from '../components/ProgressBar';

const { FiArrowLeft, FiArrowRight, FiHome } = FiIcons;

function ApplicationForm() {
  const navigate = useNavigate();
  const { state, dispatch } = useApplication();
  const { currentStep, totalSteps, formData, isSubmitting, errors } = state;

  const steps = [
    { component: PersonalInfoStep, title: 'Personal Information' },
    { component: ContactInfoStep, title: 'Contact Information' },
    { component: EmploymentInfoStep, title: 'Employment Information' },
    { component: AccountInfoStep, title: 'Account & Verification' },
  ];

  const CurrentStepComponent = steps[currentStep - 1].component;

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1: // Personal Information
        if (!formData.title) newErrors.title = 'Title is required';
        if (!formData.firstName) newErrors.firstName = 'First name is required';
        if (!formData.lastName) newErrors.lastName = 'Last name is required';
        if (!formData.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
        if (!formData.placeOfBirth) newErrors.placeOfBirth = 'Place of birth is required';
        if (!formData.gender) newErrors.gender = 'Gender is required';
        if (!formData.maritalStatus) newErrors.maritalStatus = 'Marital status is required';
        if (!formData.nationality) newErrors.nationality = 'Nationality is required';
        if (!formData.mothersMaidenName) newErrors.mothersMaidenName = 'Mother\'s maiden name is required';
        break;
        
      case 2: // Contact Information
        if (!formData.residentialAddress) newErrors.residentialAddress = 'Residential address is required';
        if (!formData.city) newErrors.city = 'City is required';
        if (!formData.region) newErrors.region = 'Region is required';
        if (!formData.country) newErrors.country = 'Country is required';
        if (!formData.phoneNumber) newErrors.phoneNumber = 'Phone number is required';
        if (!formData.email) newErrors.email = 'Email is required';
        // Email validation
        if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          newErrors.email = 'Please enter a valid email address';
        }
        // Phone validation
        if (formData.phoneNumber && !/^\+233\s?\d{2}\s?\d{3}\s?\d{4}$/.test(formData.phoneNumber)) {
          newErrors.phoneNumber = 'Please enter a valid Ghana phone number (+233 XX XXX XXXX)';
        }
        break;
        
      case 3: // Employment Information
        if (!formData.occupation) newErrors.occupation = 'Occupation is required';
        if (!formData.employer) newErrors.employer = 'Employer/Business name is required';
        if (!formData.workAddress) newErrors.workAddress = 'Work address is required';
        if (!formData.monthlyIncome) newErrors.monthlyIncome = 'Monthly income is required';
        if (!formData.sourceOfIncome) newErrors.sourceOfIncome = 'Source of income is required';
        if (!formData.nextOfKinName) newErrors.nextOfKinName = 'Next of kin name is required';
        if (!formData.nextOfKinRelationship) newErrors.nextOfKinRelationship = 'Next of kin relationship is required';
        if (!formData.nextOfKinPhone) newErrors.nextOfKinPhone = 'Next of kin phone is required';
        if (!formData.nextOfKinAddress) newErrors.nextOfKinAddress = 'Next of kin address is required';
        break;
        
      case 4: // Account Information
        if (!formData.accountType) newErrors.accountType = 'Account type is required';
        if (!formData.initialDeposit) newErrors.initialDeposit = 'Initial deposit amount is required';
        if (!formData.preferredBranch) newErrors.preferredBranch = 'Preferred branch is required';
        if (!formData.idType) newErrors.idType = 'ID type is required';
        if (!formData.idNumber) newErrors.idNumber = 'ID number is required';
        if (!formData.idExpiryDate) newErrors.idExpiryDate = 'ID expiry date is required';
        if (!formData.termsAccepted) newErrors.termsAccepted = 'You must accept the terms and conditions';
        
        // Validate initial deposit amount
        const minDeposit = formData.accountType === 'savings' ? 10 : 
                          formData.accountType === 'current' ? 100 : 1000;
        if (formData.initialDeposit && parseFloat(formData.initialDeposit) < minDeposit) {
          newErrors.initialDeposit = `Minimum deposit for ${formData.accountType} account is GHS ${minDeposit}`;
        }
        break;
    }
    
    return newErrors;
  };

  const handleNext = () => {
    const stepErrors = validateStep(currentStep);
    
    if (Object.keys(stepErrors).length > 0) {
      dispatch({ type: 'SET_ERRORS', payload: stepErrors });
      // Scroll to first error
      const firstErrorElement = document.querySelector('.border-red-500');
      if (firstErrorElement) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }
    
    dispatch({ type: 'SET_ERRORS', payload: {} });
    
    if (currentStep < totalSteps) {
      dispatch({ type: 'SET_CURRENT_STEP', payload: currentStep + 1 });
    }
  };

  const handlePrevious = () => {
    dispatch({ type: 'SET_ERRORS', payload: {} });
    if (currentStep > 1) {
      dispatch({ type: 'SET_CURRENT_STEP', payload: currentStep - 1 });
    }
  };

  const handleSubmit = async () => {
    const stepErrors = validateStep(currentStep);
    
    if (Object.keys(stepErrors).length > 0) {
      dispatch({ type: 'SET_ERRORS', payload: stepErrors });
      return;
    }
    
    dispatch({ type: 'SET_SUBMITTING', payload: true });
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));
    dispatch({ type: 'SET_SUBMITTING', payload: false });
    navigate('/success');
  };

  return (
    <div className="min-h-screen bg-gray-50 w-full max-w-full overflow-x-hidden">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50 w-full max-w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <img 
                src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1750826998135-logo-dark.png" 
                alt="Agric Bank Ghana" 
                className="h-8 sm:h-10 w-auto flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <h1 className="text-sm sm:text-lg font-bold text-agric-primary truncate">Account Application</h1>
                <p className="text-xs sm:text-sm text-gray-600 truncate">{steps[currentStep - 1].title}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-1 sm:gap-2 text-agric-primary hover:text-agric-secondary transition-colors flex-shrink-0 ml-2"
            >
              <SafeIcon icon={FiHome} className="w-4 h-4 sm:w-5 sm:h-5" />
              <span className="hidden sm:inline text-sm">Home</span>
            </button>
          </div>
        </div>
      </header>

      <div className="w-full max-w-full overflow-x-hidden">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
          {/* Progress Bar */}
          <div className="w-full mb-8 max-w-full">
            <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
          </div>

          {/* Form Container */}
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-lg shadow-lg p-4 sm:p-6 md:p-8 w-full max-w-full overflow-x-hidden"
          >
            <div className="mb-6 sm:mb-8 w-full">
              <h2 className="text-xl sm:text-2xl font-bold text-agric-primary mb-2">
                {steps[currentStep - 1].title}
              </h2>
              <p className="text-sm sm:text-base text-gray-600">
                Step {currentStep} of {totalSteps} - Please fill in all required fields
              </p>
            </div>

            {/* Current Step Component */}
            <div className="w-full max-w-full overflow-x-hidden">
              <CurrentStepComponent />
            </div>

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 sm:mt-8 pt-6 border-t border-gray-200 w-full">
              <button
                onClick={handlePrevious}
                disabled={currentStep === 1}
                className={`w-full sm:w-auto flex items-center justify-center gap-2 px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-300 text-sm ${
                  currentStep === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <SafeIcon icon={FiArrowLeft} className="w-4 h-4 sm:w-5 sm:h-5" />
                Previous
              </button>

              <div className="text-xs sm:text-sm text-gray-500 order-first sm:order-none">
                {currentStep} of {totalSteps}
              </div>

              {currentStep === totalSteps ? (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`w-full sm:w-auto flex items-center justify-center gap-2 px-6 sm:px-8 py-3 rounded-lg font-medium transition-all duration-300 text-sm ${
                    isSubmitting
                      ? 'bg-gray-400 text-white cursor-not-allowed'
                      : 'bg-agric-primary text-white hover:bg-agric-secondary transform hover:scale-105'
                  }`}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Application'}
                  {!isSubmitting && <SafeIcon icon={FiArrowRight} className="w-4 h-4 sm:w-5 sm:h-5" />}
                </button>
              ) : (
                <button
                  onClick={handleNext}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-agric-primary text-white rounded-lg font-medium hover:bg-agric-secondary transition-all duration-300 transform hover:scale-105 text-sm"
                >
                  Next
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}

export default ApplicationForm;